import { useState, type CSSProperties, type FC } from 'react';
import type {
  ProjectMemberJointArticle,
  ProjectMemberProfile,
} from '../types';

interface ProjectMemberArticlesOverlayProps {
  isOpen: boolean;
  profile: ProjectMemberProfile | null;
  articles: ProjectMemberJointArticle[];
  onClose: () => void;
  onCreateArticle: (payload: Partial<ProjectMemberJointArticle>) => Promise<void>;
  onUpdateArticle: (articleId: string, payload: Partial<ProjectMemberJointArticle>) => Promise<void>;
}

function stageAccent(stage: string): string {
  switch (stage) {
    case 'Опубликовано / 2026':
      return '#49ddb7';
    case 'На рассмотрении':
      return '#e2ad3a';
    case 'Рецензирование':
    case 'Peer review':
      return '#41d2ff';
    case 'Редакционный процесс':
      return '#9e80ff';
    default:
      return '#8faebb';
  }
}

/** Joint articles — cab-overlay chrome + cabinet-* body. */
export const ProjectMemberArticlesOverlay: FC<ProjectMemberArticlesOverlayProps> = ({
  isOpen,
  profile,
  articles,
  onClose,
  onCreateArticle,
  onUpdateArticle,
}) => {
  const [selectedArticle, setSelectedArticle] = useState<ProjectMemberJointArticle | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [journal, setJournal] = useState('');
  const [stage, setStage] = useState('Рабочая база');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleCreateSubmit = async () => {
    if (!title.trim()) return;
    setIsSubmitting(true);
    try {
      await onCreateArticle({
        title: title.trim(),
        journal: journal.trim() || 'Scopus Q2 Journal',
        stage,
        coauthors: 'Владелец + Алтын Бақытжанова',
        payment_state: 'Уточняется',
      });
      setTitle('');
      setJournal('');
      setIsCreateModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Публикации</small>
              <h2>Совместные статьи</h2>
              <p>{profile?.full_name || 'Участник проекта'} · совместные заказы</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button
              type="button"
              className="cab-btn primary"
              onClick={() => setIsCreateModalOpen(true)}
            >
              + Новый заказ
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cab-body">
          <section className="cabinet-hero" style={{ '--accent': '#41d2ff' } as CSSProperties}>
            <div>
              <div className="cabinet-eyebrow">Файлы и хранилище · Совместные статьи</div>
              <h2>Совместные статьи / заказы</h2>
              <p>
                Общая рабочая карточка владельца и научного партнёра. Изменения сохраняются в общей
                записи.
              </p>
            </div>
          </section>

          <article
            className="cabinet-card"
            style={{ '--accent': '#e2ad3a', marginTop: 14 } as CSSProperties}
          >
            <div className="cabinet-actions">
              <div>
                <strong>Связанный реестр: «Заказы клиентов»</strong>
                <p style={{ margin: '8px 0 0', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
                  Каталог концепций Казахстана 2021–2025 и связанные карточки заказов помогают организовать работу над статьёй.
                </p>
              </div>
              <button type="button" className="cabinet-btn">
                Открыть исходную базу
              </button>
            </div>
          </article>

          <section className="cabinet-kpis" style={{ marginTop: 14 }}>
            <article className="cabinet-kpi" style={{ '--accent': '#8faebb' } as CSSProperties}>
              <small>Все карточки</small>
              <strong>{articles.length || 5}</strong>
            </article>
            <article className="cabinet-kpi" style={{ '--accent': '#41d2ff' } as CSSProperties}>
              <small>Этапы</small>
              <strong>5</strong>
            </article>
            <article className="cabinet-kpi" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
              <small>В процессе</small>
              <strong>3</strong>
            </article>
            <article className="cabinet-kpi" style={{ '--accent': '#49ddb7' } as CSSProperties}>
              <small>Совместных редактора</small>
              <strong>2</strong>
            </article>
          </section>

          <section className="cabinet-stack" style={{ marginTop: 16 }}>
            {articles.map((art) => (
              <article
                key={art.id}
                className="cabinet-card"
                style={{ '--accent': stageAccent(art.stage) } as CSSProperties}
              >
                <div className="cabinet-actions">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: 'block' }}>{art.title}</strong>
                    <span style={{ color: '#8faebb', fontSize: 12 }}>
                      {art.journal || 'Журнал'}
                    </span>
                  </div>
                  <span
                    className="cabinet-badge"
                    style={{ '--badge': stageAccent(art.stage) } as CSSProperties}
                  >
                    {art.stage === 'Peer review' ? 'Рецензирование' : art.stage}
                  </span>
                  <div style={{ textAlign: 'right', fontSize: 12 }}>
                    <span style={{ display: 'block', color: '#8faebb', fontSize: 10 }}>Авторы</span>
                    <span>{art.coauthors}</span>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12 }}>
                    <span style={{ display: 'block', color: '#8faebb', fontSize: 10 }}>Оплата</span>
                    <span style={{ color: '#e2ad3a' }}>{art.payment_state || 'Уточняется'}</span>
                  </div>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setSelectedArticle(art)}
                  >
                    Открыть
                  </button>
                </div>
              </article>
            ))}
          </section>
        </div>
      </div>

      {selectedArticle ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 640 }}>
            <header className="cabinet-modal-head">
              <div>
                <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                  {selectedArticle.article_code}
                </span>
                <h2>{selectedArticle.title}</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setSelectedArticle(null)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </header>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <article className="cabinet-card" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                  <small style={{ color: '#8faebb' }}>Целевой журнал / сборник</small>
                  <strong style={{ display: 'block', marginTop: 4 }}>{selectedArticle.journal}</strong>
                </article>
                <div className="cabinet-grid2">
                  <article className="cabinet-card" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                    <small style={{ color: '#8faebb' }}>Текущий этап</small>
                    <strong style={{ display: 'block', marginTop: 4, color: '#41d2ff' }}>
                      {selectedArticle.stage === 'Peer review'
                        ? 'Рецензирование'
                        : selectedArticle.stage}
                    </strong>
                  </article>
                  <article className="cabinet-card" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
                    <small style={{ color: '#8faebb' }}>Статус взаиморасчётов</small>
                    <strong style={{ display: 'block', marginTop: 4, color: '#e2ad3a' }}>
                      {selectedArticle.payment_state}
                    </strong>
                  </article>
                </div>
                <article className="cabinet-card" style={{ '--accent': '#9e80ff' } as CSSProperties}>
                  <small style={{ color: '#8faebb' }}>Команда авторов</small>
                  <strong style={{ display: 'block', marginTop: 4 }}>{selectedArticle.coauthors}</strong>
                </article>
                <div className="cabinet-actions" style={{ justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    className="cabinet-btn primary"
                    onClick={() => {
                      void onUpdateArticle(selectedArticle.id, {
                        stage: 'Редакционный процесс',
                      }).then(() => {
                        setSelectedArticle({
                          ...selectedArticle,
                          stage: 'Редакционный процесс',
                        });
                      });
                    }}
                  >
                    Перевести в редакционный процесс
                  </button>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setSelectedArticle(null)}
                  >
                    Закрыть
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isCreateModalOpen ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 520 }}>
            <header className="cabinet-modal-head">
              <div>
                <h2>Новый заказ на статью</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsCreateModalOpen(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </header>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Тема статьи</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Например: Linguistic Landscape of Astana"
                  />
                </label>
                <label className="cabinet-field">
                  <span>Журнал / направление</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={journal}
                    onChange={(e) => setJournal(e.target.value)}
                    placeholder="Scopus Q1/Q2"
                  />
                </label>
                <label className="cabinet-field">
                  <span>Стадия</span>
                  <select
                    className="cabinet-select"
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                  >
                    <option value="Рабочая база">Рабочая база</option>
                    <option value="Редакционный процесс">Редакционный процесс</option>
                    <option value="Рецензирование">Рецензирование</option>
                    <option value="На рассмотрении">На рассмотрении</option>
                    <option value="Опубликовано / 2026">Опубликовано / 2026</option>
                  </select>
                </label>
                <div className="cabinet-actions" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setIsCreateModalOpen(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="cabinet-btn primary"
                    disabled={isSubmitting || !title.trim()}
                    onClick={() => void handleCreateSubmit()}
                  >
                    Создать заказ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
